import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { createCompetition } from '../services/organizer-api';
import Container from '../components/layout/Container';
import Button from '../components/common/Button';
import './CreateCompetition.css';

/**
 * CreateCompetition
 * Form for organisers to create new competitions
 */
export default function CreateCompetition() {
  const navigate = useNavigate();
  const { user, isOrganizer } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    competitionType: 'league',
    knockoutSubtype: '',
    drawMethod: 'random',
    playersPerTeam: 7,
    startDate: '',
    endDate: '',
    weeklyMatchDay: 'Monday',
    pointsPerGame: 1,
    bonusPointsPerMatch: 0,
  });

  const [divisions, setDivisions] = useState([]);
  const [divisionInput, setDivisionInput] = useState('');

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const addDivision = () => {
    const trimmedDivision = divisionInput.trim();
    if (trimmedDivision && !divisions.includes(trimmedDivision)) {
      setDivisions([...divisions, trimmedDivision]);
      setDivisionInput('');
    }
  };

  const removeDivision = (divisionToRemove) => {
    setDivisions(divisions.filter(d => d !== divisionToRemove));
  };

  const handleDivisionInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addDivision();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (divisions.length === 0) {
        setError('Please add at least one division');
        setLoading(false);
        return;
      }

      const competitionData = {
        name: formData.name,
        competitionType: formData.competitionType,
        knockoutSubtype: formData.knockoutSubtype || undefined,
        drawMethod: formData.drawMethod,
        playersPerTeam: formData.playersPerTeam,
        divisions,
        startDate: formData.startDate,
        endDate: formData.endDate || undefined,
        weeklyMatchDay: formData.weeklyMatchDay || undefined,
        pointsPerGame: formData.pointsPerGame,
        bonusPointsPerMatch: formData.bonusPointsPerMatch,
      };

      // Call API to create competition
      const newCompetition = await createCompetition(competitionData);

      console.log('Competition created:', newCompetition);

      // Redirect to organiser dashboard on success
      navigate('/organizer/dashboard');
    } catch (err) {
      console.error('Failed to create competition:', err);
      setError(err.message || 'Failed to create competition');
    } finally {
      setLoading(false);
    }
  };

  if (!user || !isOrganizer) {
    return (
      <Container>
        <div className="auth-required">
          <h2>Access Denied</h2>
          <p>This page is only accessible to competition organisers.</p>
          <Button onClick={() => navigate('/')}>Return Home</Button>
        </div>
      </Container>
    );
  }

  return (
    <Container maxWidth="narrow">
      <div className="create-competition">
        <div className="page-header">
          <h1>Create Competition</h1>
          <p className="page-description">
            Set up a new pool tournament or league
          </p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="competition-form">
          {/* Basic Information */}
          <section className="form-section">
            <h2>Basic Information</h2>

            <div className="form-group">
              <label htmlFor="name">Competition Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g. City Pool League - Winter 2025"
              />
            </div>

            <div className="form-group">
              <label htmlFor="competitionType">Competition Type *</label>
              <select
                id="competitionType"
                name="competitionType"
                value={formData.competitionType}
                onChange={handleChange}
                required
              >
                <option value="league">League</option>
                <option value="knockout">Knockout</option>
                <option value="bespoke">Bespoke</option>
              </select>
            </div>

            {formData.competitionType === 'knockout' && (
              <div className="form-group">
                <label htmlFor="knockoutSubtype">Knockout Format</label>
                <select
                  id="knockoutSubtype"
                  name="knockoutSubtype"
                  value={formData.knockoutSubtype}
                  onChange={handleChange}
                >
                  <option value="">Select format</option>
                  <option value="roundRobin">Round Robin</option>
                  <option value="straightKnockout">Straight Knockout</option>
                  <option value="doubleElimination">Double Elimination</option>
                </select>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="divisions">Divisions *</label>
              <div className="division-input-group">
                <input
                  type="text"
                  id="divisions"
                  value={divisionInput}
                  onChange={(e) => setDivisionInput(e.target.value)}
                  onKeyPress={handleDivisionInputKeyPress}
                  placeholder="e.g. Premier"
                />
                <Button
                  type="button"
                  onClick={addDivision}
                  variant="secondary"
                  disabled={!divisionInput.trim()}
                >
                  Add Division
                </Button>
              </div>
              {divisions.length > 0 && (
                <div className="divisions-list">
                  {divisions.map((division, index) => (
                    <div key={index} className="division-tag">
                      <span>{division}</span>
                      <button
                        type="button"
                        onClick={() => removeDivision(division)}
                        className="remove-btn"
                        aria-label={`Remove ${division}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {divisions.length === 0 && (
                <small className="hint-text">Add at least one division (e.g., Premier, Division One, Division Two)</small>
              )}
            </div>
          </section>

          {/* Teams & Players */}
          <section className="form-section">
            <h2>Teams & Players</h2>

            <div className="form-group">
              <label htmlFor="playersPerTeam">Players Per Team *</label>
              <input
                type="number"
                id="playersPerTeam"
                name="playersPerTeam"
                value={formData.playersPerTeam}
                onChange={handleChange}
                required
                min="1"
                max="20"
              />
            </div>

            <div className="form-group">
              <label htmlFor="drawMethod">Draw Method *</label>
              <select
                id="drawMethod"
                name="drawMethod"
                value={formData.drawMethod}
                onChange={handleChange}
                required
              >
                <option value="random">Random</option>
                <option value="physical">Physical (Manual)</option>
              </select>
            </div>
          </section>

          {/* Schedule */}
          <section className="form-section">
            <h2>Schedule</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startDate">Start Date *</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="endDate">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                />
              </div>
            </div>

            {formData.competitionType === 'league' && (
              <div className="form-group">
                <label htmlFor="weeklyMatchDay">Weekly Match Day</label>
                <select
                  id="weeklyMatchDay"
                  name="weeklyMatchDay"
                  value={formData.weeklyMatchDay}
                  onChange={handleChange}
                >
                  <option value="Monday">Monday</option>
                  <option value="Tuesday">Tuesday</option>
                  <option value="Wednesday">Wednesday</option>
                  <option value="Thursday">Thursday</option>
                  <option value="Friday">Friday</option>
                  <option value="Saturday">Saturday</option>
                  <option value="Sunday">Sunday</option>
                </select>
              </div>
            )}
          </section>

          {/* Scoring */}
          <section className="form-section">
            <h2>Scoring</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="pointsPerGame">Points Per Game *</label>
                <input
                  type="number"
                  id="pointsPerGame"
                  name="pointsPerGame"
                  value={formData.pointsPerGame}
                  onChange={handleChange}
                  required
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="bonusPointsPerMatch">Bonus Points Per Match</label>
                <input
                  type="number"
                  id="bonusPointsPerMatch"
                  name="bonusPointsPerMatch"
                  value={formData.bonusPointsPerMatch}
                  onChange={handleChange}
                  min="0"
                />
              </div>
            </div>
          </section>

          {/* Form Actions */}
          <div className="form-actions">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/organizer/dashboard')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Competition'}
            </Button>
          </div>
        </form>

      </div>
    </Container>
  );
}
